import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentProjectFiles.css";

const EMPTY_FORM = {
  client_id: "",
  project_id: "",
  document_id: "",
  file_name: "",
  file_url: "",
  file_category: "other",
  mime_type: "",
  file_size_bytes: "",
  description: "",
  final_deliverable: false,
};

const FILE_CATEGORIES = [
  "agreement",
  "proposal",
  "invoice",
  "receipt",
  "design",
  "image",
  "document",
  "source_code",
  "backup",
  "deliverable",
  "other",
];

function formatLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatFileSize(value) {
  const bytes = Number(value);

  if (
    !Number.isFinite(bytes) ||
    bytes <= 0
  ) {
    return "Unknown size";
  }

  const units = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB",
  ];

  let size = bytes;
  let unitIndex = 0;

  while (
    size >= 1024 &&
    unitIndex < units.length - 1
  ) {
    size /= 1024;
    unitIndex += 1;
  }

  const decimals =
    size >= 10 || unitIndex === 0
      ? 0
      : 1;

  return `${size.toFixed(
    decimals
  )} ${units[unitIndex]}`;
}

function getClientName(client) {
  if (!client) {
    return "Unknown client";
  }

  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${client.id}`
  );
}

function normalizeFile(file = {}) {
  return {
    ...EMPTY_FORM,
    ...file,

    client_id:
      file.client_id == null
        ? ""
        : String(file.client_id),

    project_id:
      file.project_id == null
        ? ""
        : String(file.project_id),

    document_id:
      file.document_id == null
        ? ""
        : String(file.document_id),

    file_size_bytes:
      file.file_size_bytes == null
        ? ""
        : String(file.file_size_bytes),

    final_deliverable:
      Number(file.final_deliverable) ===
      1,
  };
}

function DevelopmentProjectFiles() {
  const [files, setFiles] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [projects, setProjects] =
    useState([]);

  const [documents, setDocuments] =
    useState([]);

  const [selectedFile, setSelectedFile] =
    useState(null);

  const [formData, setFormData] =
    useState(EMPTY_FORM);

  const [showForm, setShowForm] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [clientFilter, setClientFilter] =
    useState("");

  const [projectFilter, setProjectFilter] =
    useState("");

  const [categoryFilter, setCategoryFilter] =
    useState("");

  const [
    deliverableFilter,
    setDeliverableFilter,
  ] = useState("all");

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadData = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const [
          fileResponse,
          clientResponse,
          projectResponse,
          documentResponse,
        ] = await Promise.all([
          Api.getDevelopmentProjectFiles(),
          Api.getDevelopmentClients(),
          Api.getDevelopmentProjects(),
          Api.getDevelopmentDocuments(),
        ]);

        setFiles(
          fileResponse?.files || []
        );

        setClients(
          clientResponse?.clients || []
        );

        setProjects(
          projectResponse?.projects || []
        );

        setDocuments(
          documentResponse?.documents || []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Project files could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientMap = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          Number(client.id),
          client,
        ])
      ),
    [clients]
  );

  const projectMap = useMemo(
    () =>
      new Map(
        projects.map((project) => [
          Number(project.id),
          project,
        ])
      ),
    [projects]
  );

  const documentMap = useMemo(
    () =>
      new Map(
        documents.map((document) => [
          Number(document.id),
          document,
        ])
      ),
    [documents]
  );

  const availableProjects = useMemo(() => {
    if (!formData.client_id) {
      return projects;
    }

    return projects.filter(
      (project) =>
        String(project.client_id) ===
        String(formData.client_id)
    );
  }, [
    projects,
    formData.client_id,
  ]);

  const availableDocuments = useMemo(() => {
    return documents.filter((document) => {
      if (
        formData.client_id &&
        String(document.client_id) !==
          String(formData.client_id)
      ) {
        return false;
      }

      if (
        formData.project_id &&
        String(document.project_id) !==
          String(formData.project_id)
      ) {
        return false;
      }

      return true;
    });
  }, [
    documents,
    formData.client_id,
    formData.project_id,
  ]);

  const filterProjects = useMemo(() => {
    if (!clientFilter) {
      return projects;
    }

    return projects.filter(
      (project) =>
        String(project.client_id) ===
        String(clientFilter)
    );
  }, [
    projects,
    clientFilter,
  ]);

  const filteredFiles = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    return files.filter((file) => {
      if (
        clientFilter &&
        String(file.client_id) !==
          String(clientFilter)
      ) {
        return false;
      }

      if (
        projectFilter &&
        String(file.project_id) !==
          String(projectFilter)
      ) {
        return false;
      }

      if (
        categoryFilter &&
        String(file.file_category) !==
          String(categoryFilter)
      ) {
        return false;
      }

      if (
        deliverableFilter ===
          "deliverable" &&
        Number(file.final_deliverable) !==
          1
      ) {
        return false;
      }

      if (
        deliverableFilter ===
          "regular" &&
        Number(file.final_deliverable) ===
          1
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const client =
        clientMap.get(
          Number(file.client_id)
        );

      const project =
        projectMap.get(
          Number(file.project_id)
        );

      const document =
        documentMap.get(
          Number(file.document_id)
        );

      const searchableText = [
        file.file_name,
        file.file_url,
        file.file_category,
        file.mime_type,
        file.description,
        getClientName(client),
        project?.title,
        document?.title,
        document?.document_number,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(
        normalizedSearch
      );
    });
  }, [
    files,
    search,
    clientFilter,
    projectFilter,
    categoryFilter,
    deliverableFilter,
    clientMap,
    projectMap,
    documentMap,
  ]);

  const summary = useMemo(
    () => ({
      total: files.length,

      deliverables: files.filter(
        (file) =>
          Number(
            file.final_deliverable
          ) === 1
      ).length,

      regular: files.filter(
        (file) =>
          Number(
            file.final_deliverable
          ) !== 1
      ).length,

      totalBytes: files.reduce(
        (total, file) =>
          total +
          (
            Number(
              file.file_size_bytes
            ) || 0
          ),
        0
      ),
    }),
    [files]
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewFileForm = () => {
    clearMessages();
    setSelectedFile(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditFileForm = (
    file
  ) => {
    clearMessages();
    setSelectedFile(file);
    setFormData(normalizeFile(file));
    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeForm = () => {
    setSelectedFile(null);
    setFormData(EMPTY_FORM);
    setShowForm(false);
  };

  const handleChange = (event) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setFormData((current) => {
      const next = {
        ...current,
        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      if (name === "client_id") {
        next.project_id = "";
        next.document_id = "";
      }

      if (name === "project_id") {
        next.document_id = "";
      }

      return next;
    });
  };

  const validateForm = () => {
    if (!formData.client_id) {
      return "A client is required.";
    }

    if (!formData.project_id) {
      return "A project is required.";
    }

    if (!formData.file_name.trim()) {
      return "File name is required.";
    }

    if (!formData.file_url.trim()) {
      return "File URL is required.";
    }

    if (formData.file_size_bytes) {
      const size = Number(
        formData.file_size_bytes
      );

      if (
        !Number.isInteger(size) ||
        size < 0
      ) {
        return "File size must be a whole number that is 0 or greater.";
      }
    }

    return "";
  };

  const buildPayload = () => ({
    client_id:
      Number(formData.client_id),

    project_id:
      Number(formData.project_id),

    document_id:
      formData.document_id
        ? Number(
            formData.document_id
          )
        : null,

    file_name:
      formData.file_name.trim(),

    file_url:
      formData.file_url.trim(),

    file_category:
      formData.file_category || null,

    mime_type:
      formData.mime_type.trim() ||
      null,

    file_size_bytes:
      formData.file_size_bytes === ""
        ? null
        : Number(
            formData.file_size_bytes
          ),

    description:
      formData.description.trim() ||
      null,

    final_deliverable:
      Boolean(
        formData.final_deliverable
      ),
  });

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();
    clearMessages();

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const payload =
        buildPayload();

      if (selectedFile) {
        await Api.updateDevelopmentProjectFile(
          selectedFile.id,
          payload
        );

        setSuccess(
          "Project file updated successfully."
        );
      } else {
        await Api.createDevelopmentProjectFile(
          payload
        );

        setSuccess(
          "Project file created successfully."
        );
      }

      await loadData();
      closeForm();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The project file could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDeliverable = async (
    file
  ) => {
    clearMessages();

    const nextValue =
      Number(file.final_deliverable) !==
      1;

    try {
      await Api.updateDevelopmentProjectFile(
        file.id,
        {
          final_deliverable:
            nextValue,
        }
      );

      setSuccess(
        nextValue
          ? "File marked as a final deliverable."
          : "File removed from final deliverables."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The file could not be updated."
      );
    }
  };

  const deleteFile = async (
    file
  ) => {
    const confirmed =
      window.confirm(
        `Delete file record "${file.file_name}"?`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    try {
      await Api.deleteDevelopmentProjectFile(
        file.id
      );

      setSuccess(
        "Project file deleted successfully."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The project file could not be deleted."
      );
    }
  };

  return (
    <div className="development-project-files-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            File management
          </p>

          <h2>Project Files</h2>

          <p>
            Track project documents, assets,
            source files, backups, and final
            client deliverables.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={openNewFileForm}
          >
            Add file
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showForm && (
        <ProjectFileForm
          formData={formData}
          clients={clients}
          projects={
            availableProjects
          }
          documents={
            availableDocuments
          }
          selectedFile={
            selectedFile
          }
          isSaving={isSaving}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      <section className="development-project-file-summary-grid">
        <SummaryCard
          label="All files"
          value={summary.total}
          active={
            deliverableFilter === "all"
          }
          onClick={() =>
            setDeliverableFilter("all")
          }
        />

        <SummaryCard
          label="Final deliverables"
          value={summary.deliverables}
          active={
            deliverableFilter ===
            "deliverable"
          }
          onClick={() =>
            setDeliverableFilter(
              "deliverable"
            )
          }
        />

        <SummaryCard
          label="Other files"
          value={summary.regular}
          active={
            deliverableFilter ===
            "regular"
          }
          onClick={() =>
            setDeliverableFilter(
              "regular"
            )
          }
        />

        <SummaryCard
          label="Tracked storage"
          value={formatFileSize(
            summary.totalBytes
          )}
          active={false}
          onClick={() =>
            setDeliverableFilter("all")
          }
        />
      </section>

      <section className="development-project-files-panel">
        <div className="development-project-files-toolbar">
          <div>
            <h3>File records</h3>

            <p>
              {filteredFiles.length} file
              {filteredFiles.length === 1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-project-file-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search files..."
              aria-label="Search files"
            />

            <select
              value={clientFilter}
              onChange={(event) => {
                setClientFilter(
                  event.target.value
                );
                setProjectFilter("");
              }}
            >
              <option value="">
                All clients
              </option>

              {clients.map((client) => (
                <option
                  key={client.id}
                  value={client.id}
                >
                  {getClientName(client)}
                </option>
              ))}
            </select>

            <select
              value={projectFilter}
              onChange={(event) =>
                setProjectFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All projects
              </option>

              {filterProjects.map(
                (project) => (
                  <option
                    key={project.id}
                    value={project.id}
                  >
                    {project.title}
                  </option>
                )
              )}
            </select>

            <select
              value={categoryFilter}
              onChange={(event) =>
                setCategoryFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All categories
              </option>

              {FILE_CATEGORIES.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {formatLabel(category)}
                  </option>
                )
              )}
            </select>

            <select
              value={deliverableFilter}
              onChange={(event) =>
                setDeliverableFilter(
                  event.target.value
                )
              }
            >
              <option value="all">
                All files
              </option>

              <option value="deliverable">
                Final deliverables
              </option>

              <option value="regular">
                Other files
              </option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div
            className="development-admin-state"
            role="status"
          >
            Loading project files...
          </div>
        ) : filteredFiles.length ===
          0 ? (
          <div className="development-admin-empty">
            No project files match the current
            filters.
          </div>
        ) : (
          <div className="development-project-file-card-grid">
            {filteredFiles.map((file) => (
              <ProjectFileCard
                key={file.id}
                file={file}
                client={clientMap.get(
                  Number(file.client_id)
                )}
                project={projectMap.get(
                  Number(file.project_id)
                )}
                document={documentMap.get(
                  Number(file.document_id)
                )}
                onEdit={() =>
                  openEditFileForm(file)
                }
                onToggleDeliverable={() =>
                  toggleDeliverable(file)
                }
                onDelete={() =>
                  deleteFile(file)
                }
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-project-file-summary-card${
        active ? " active" : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function ProjectFileCard({
  file,
  client,
  project,
  document,
  onEdit,
  onToggleDeliverable,
  onDelete,
}) {
  const isDeliverable =
    Number(file.final_deliverable) ===
    1;

  return (
    <article
      className={`development-project-file-card${
        isDeliverable
          ? " deliverable"
          : ""
      }`}
    >
      <div className="development-project-file-card-header">
        <div>
          <p>
            {formatLabel(
              file.file_category ||
                "other"
            )}
          </p>

          <h4>{file.file_name}</h4>

          <span>
            {getClientName(client)}
          </span>
        </div>

        {isDeliverable && (
          <span className="development-project-file-deliverable-badge">
            Final Deliverable
          </span>
        )}
      </div>

      <div className="development-project-file-meta-grid">
        <div>
          <span>Project</span>

          <strong>
            {project?.title || "—"}
          </strong>
        </div>

        <div>
          <span>Document</span>

          <strong>
            {document?.title || "—"}
          </strong>
        </div>

        <div>
          <span>Type</span>

          <strong>
            {file.mime_type ||
              "Unknown"}
          </strong>
        </div>

        <div>
          <span>Size</span>

          <strong>
            {formatFileSize(
              file.file_size_bytes
            )}
          </strong>
        </div>

        <div>
          <span>Created</span>

          <strong>
            {formatDate(
              file.created_at
            )}
          </strong>
        </div>

        <div>
          <span>Updated</span>

          <strong>
            {formatDate(
              file.updated_at
            )}
          </strong>
        </div>
      </div>

      <p className="development-project-file-description">
        {file.description ||
          "No file description has been added."}
      </p>

      <a
        href={file.file_url}
        target="_blank"
        rel="noreferrer"
        className="development-project-file-open-link"
      >
        Open file
      </a>

      <div className="development-project-file-card-actions">
        <button
          type="button"
          onClick={onEdit}
        >
          Edit
        </button>

        <button
          type="button"
          className={
            isDeliverable
              ? "remove-deliverable"
              : "make-deliverable"
          }
          onClick={
            onToggleDeliverable
          }
        >
          {isDeliverable
            ? "Remove deliverable"
            : "Make deliverable"}
        </button>

        <button
          type="button"
          className="delete"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </article>
  );
}

function ProjectFileForm({
  formData,
  clients,
  projects,
  documents,
  selectedFile,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-project-file-form-panel">
      <div className="development-project-file-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedFile
              ? "Edit file"
              : "New file"}
          </p>

          <h3>
            {selectedFile
              ? selectedFile.file_name
              : "Add project file"}
          </h3>
        </div>

        <button
          type="button"
          className="development-project-file-close-button"
          onClick={onCancel}
          aria-label="Close project file form"
        >
          ×
        </button>
      </div>

      <form
        className="development-project-file-form"
        onSubmit={onSubmit}
      >
        <div className="development-project-file-form-grid">
          <label className="development-project-file-field">
            <span>Client *</span>

            <select
              name="client_id"
              value={formData.client_id}
              onChange={onChange}
              required
            >
              <option value="">
                Select client
              </option>

              {clients.map((client) => (
                <option
                  key={client.id}
                  value={client.id}
                >
                  {getClientName(client)}
                </option>
              ))}
            </select>
          </label>

          <label className="development-project-file-field">
            <span>Project *</span>

            <select
              name="project_id"
              value={formData.project_id}
              onChange={onChange}
              required
            >
              <option value="">
                Select project
              </option>

              {projects.map((project) => (
                <option
                  key={project.id}
                  value={project.id}
                >
                  {project.title}
                </option>
              ))}
            </select>
          </label>

          <label className="development-project-file-field">
            <span>Linked document</span>

            <select
              name="document_id"
              value={formData.document_id}
              onChange={onChange}
            >
              <option value="">
                No linked document
              </option>

              {documents.map(
                (document) => (
                  <option
                    key={document.id}
                    value={document.id}
                  >
                    {document.document_number
                      ? `${document.document_number} — `
                      : ""}
                    {document.title}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-project-file-field">
            <span>File category</span>

            <select
              name="file_category"
              value={
                formData.file_category
              }
              onChange={onChange}
            >
              {FILE_CATEGORIES.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {formatLabel(category)}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-project-file-field full-width">
            <span>File name *</span>

            <input
              type="text"
              name="file_name"
              value={formData.file_name}
              onChange={onChange}
              required
            />
          </label>

          <label className="development-project-file-field full-width">
            <span>File URL *</span>

            <input
              type="url"
              name="file_url"
              value={formData.file_url}
              onChange={onChange}
              placeholder="https://"
              required
            />
          </label>

          <label className="development-project-file-field">
            <span>MIME type</span>

            <input
              type="text"
              name="mime_type"
              value={formData.mime_type}
              onChange={onChange}
              placeholder="application/pdf"
            />
          </label>

          <label className="development-project-file-field">
            <span>File size in bytes</span>

            <input
              type="number"
              name="file_size_bytes"
              value={
                formData.file_size_bytes
              }
              onChange={onChange}
              min="0"
              step="1"
            />
          </label>

          <label className="development-project-file-field full-width">
            <span>Description</span>

            <textarea
              name="description"
              value={formData.description}
              onChange={onChange}
              rows="5"
            />
          </label>
        </div>

        <label className="development-project-file-checkbox">
          <input
            type="checkbox"
            name="final_deliverable"
            checked={
              formData.final_deliverable
            }
            onChange={onChange}
          />

          <span>
            This file is a final client
            deliverable
          </span>
        </label>

        <div className="development-project-file-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedFile
                ? "Save changes"
                : "Create file record"}
          </button>
        </div>
      </form>
    </section>
  );
}

export default DevelopmentProjectFiles;